import type { ReactNode, Dispatch } from "react";
import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
} from "react";

// ============================================================================
// TYPES
// ============================================================================

export type ActionType = string;

export interface BaseAction {
  type: ActionType;
  payload?: unknown;
}

export interface StateConfig {
  [key: string]: {
    default: unknown;
    actions?: ActionConfig[];
  };
}

export interface ActionConfig {
  name: string;
  type: ActionType;
  reducer: (state: unknown, payload: unknown) => unknown;
}

export interface ContextConfig<TState, TActions = Record<string, unknown>> {
  name: string;
  state: StateConfig;
  customActions?: Record<
    string,
    (
      state: TState,
      dispatch: Dispatch<BaseAction>,
      ...args: unknown[]
    ) => unknown
  >;
  actionTypes?: TActions;
}

export interface ContextValue<TState, TActions = Record<string, unknown>> {
  state: TState;
  actions: TActions;
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Creates a state object with default values based on the configuration
 */
export function createState(config: StateConfig): Record<string, unknown> {
  const state: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(config)) {
    state[key] = value.default;
  }

  return state;
}

/**
 * Creates a reducer function based on the configuration
 */
export function createReducer(config: StateConfig) {
  return (
    state: Record<string, unknown>,
    action: BaseAction
  ): Record<string, unknown> => {
    for (const [key, value] of Object.entries(config)) {
      if (value.actions) {
        const matchingAction = value.actions.find(
          (a) => a.type === action.type
        );
        if (matchingAction) {
          return {
            ...state,
            [key]: matchingAction.reducer(state[key], action.payload),
          };
        }
      }
    }
    return state;
  };
}

/**
 * Creates action creators based on the configuration
 */
export function createActionCreators(
  config: StateConfig,
  dispatch: Dispatch<BaseAction>
) {
  const actions: Record<string, (payload?: unknown) => void> = {};

  for (const [key, value] of Object.entries(config)) {
    if (value.actions) {
      value.actions.forEach((action) => {
        actions[action.name] = useCallback(
          (payload?: unknown) => {
            dispatch({ type: action.type, payload });
          },
          [dispatch]
        );
      });
    }
  }

  return actions;
}

// ============================================================================
// CONTEXT BUILDER
// ============================================================================

/**
 * Main context builder function
 */
export function createContextBuilder<
  TState,
  TActions = Record<string, unknown>,
>(config: ContextConfig<TState, TActions>) {
  const { name, state: stateConfig, customActions = {} } = config;

  // Create initial state
  const initialState = createState(stateConfig);

  // Create reducer
  const reducer = createReducer(stateConfig);

  // Create context
  const Context = createContext<ContextValue<TState, TActions> | undefined>(
    undefined
  );

  // Create provider component
  const Provider = ({
    children,
    initialState: userInitialState,
  }: {
    children: ReactNode;
    initialState: TState;
  }) => {
    const [state, dispatch] = useReducer(reducer, {
      ...initialState,
      ...userInitialState,
    } as Record<string, unknown>);

    // Create standard action creators
    const standardActions = createActionCreators(stateConfig, dispatch);

    // Create custom actions
    const customActionCreators: Record<
      string,
      (...args: unknown[]) => unknown
    > = {};
    // TODO: Refactor this to avoid disabling react-hooks/rules-of-hooks
    // Is there any way to avoid calling useCallback inside of a loop here?
    for (const [actionName, actionCreator] of Object.entries(customActions)) {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      customActionCreators[actionName] = useCallback(
        (...args: unknown[]) =>
          actionCreator(state as TState, dispatch, ...args),
        [state, dispatch]
      );
    }

    const actions = {
      ...standardActions,
      ...customActionCreators,
    } as TActions;

    const contextValue: ContextValue<TState, TActions> = {
      state: state as TState,
      actions,
    };

    return React.createElement(
      Context.Provider,
      { value: contextValue },
      children
    );
  };

  // Create hook
  const useCustomContext = (): ContextValue<TState, TActions> => {
    const context = useContext(Context);
    if (context === undefined) {
      throw new Error(`${name} context must be used within a ${name}Provider`);
    }
    return context;
  };

  // Create convenience hooks
  const useState = (): TState => {
    const { state } = useCustomContext();
    return state;
  };

  const useActions = (): TActions => {
    const { actions } = useCustomContext();
    return actions;
  };

  return {
    Provider,
    useContext: useCustomContext,
    useState,
    useActions,
    initialState,
  };
}

// ============================================================================
// PREDEFINED ACTION PATTERNS
// ============================================================================

/**
 * Common action patterns that can be reused
 */
export const ActionPatterns = {
  // Array management
  array: {
    add: <T>(state: T[], payload: T): T[] => [payload, ...state],
    remove:
      <T>(idField: keyof T) =>
      (state: T[], payload: T[keyof T]): T[] =>
        state.filter((item) => item[idField] !== payload),
    update:
      <T>(idField: keyof T) =>
      (state: T[], payload: Partial<T>): T[] =>
        state.map((item) =>
          item[idField] === payload[idField] ? { ...item, ...payload } : item
        ),
    set: <T>(state: T[], payload: T[]): T[] => payload,
    clear: <T>(state: T[], payload: unknown): T[] => [],
  },

  // Single item management
  item: {
    set: <T>(state: T | undefined, payload: T): T => payload,
    clear: <T>(state: T | undefined, payload: unknown): undefined => undefined,
    update: <T>(state: T | undefined, payload: Partial<T>): T | Partial<T> =>
      state ? { ...state, ...payload } : payload,
  },

  // Boolean management
  boolean: {
    set: (state: boolean, payload: boolean): boolean => payload,
    toggle: (state: boolean, payload: unknown): boolean => !state,
    enable: (state: boolean, payload: unknown): boolean => true,
    disable: (state: boolean, payload: unknown): boolean => false,
  },

  // String management
  string: {
    set: (state: string, payload: string): string => payload,
    clear: (state: string, payload: unknown): string => "",
  },

  // Number management
  number: {
    set: (state: number, payload: number): number => payload,
    increment: (state: number, payload: number = 1): number => state + payload,
    decrement: (state: number, payload: number = 1): number => state - payload,
    reset: (state: number, payload: unknown): number => 0,
  },
};

// ============================================================================
// HELPER FUNCTIONS FOR COMMON PATTERNS
// ============================================================================

/**
 * Creates a simple array state configuration
 */
export function createArrayState<T>(
  name: string,
  defaultValue: T[] = [],
  idField: keyof T = "id" as keyof T
): Record<string, { default: T[]; actions: ActionConfig[] }> {
  return {
    [name]: {
      default: defaultValue,
      actions: [
        {
          name: `add${name.charAt(0).toUpperCase() + name.slice(1)}`,
          type: `ADD_${name.toUpperCase()}`,
          reducer: ActionPatterns.array.add<T> as (
            state: unknown,
            payload: unknown
          ) => unknown,
        },
        {
          name: `remove${name.charAt(0).toUpperCase() + name.slice(1)}`,
          type: `REMOVE_${name.toUpperCase()}`,
          reducer: ActionPatterns.array.remove<T>(idField) as (
            state: unknown,
            payload: unknown
          ) => unknown,
        },
        {
          name: `update${name.charAt(0).toUpperCase() + name.slice(1)}`,
          type: `UPDATE_${name.toUpperCase()}`,
          reducer: ActionPatterns.array.update<T>(idField) as (
            state: unknown,
            payload: unknown
          ) => unknown,
        },
        {
          name: `set${name.charAt(0).toUpperCase() + name.slice(1)}`,
          type: `SET_${name.toUpperCase()}`,
          reducer: ActionPatterns.array.set<T> as (
            state: unknown,
            payload: unknown
          ) => unknown,
        },
        {
          name: `clear${name.charAt(0).toUpperCase() + name.slice(1)}`,
          type: `CLEAR_${name.toUpperCase()}`,
          reducer: ActionPatterns.array.clear<T> as (
            state: unknown,
            payload: unknown
          ) => unknown,
        },
      ],
    },
  };
}

/**
 * Creates a simple item state configuration
 */
export function createItemState<T>(
  name: string,
  defaultValue?: T
): Record<string, { default: T | undefined; actions: ActionConfig[] }> {
  return {
    [name]: {
      default: defaultValue,
      actions: [
        {
          name: `set${name.charAt(0).toUpperCase() + name.slice(1)}`,
          type: `SET_${name.toUpperCase()}`,
          reducer: ActionPatterns.item.set<T> as (
            state: unknown,
            payload: unknown
          ) => unknown,
        },
        {
          name: `clear${name.charAt(0).toUpperCase() + name.slice(1)}`,
          type: `CLEAR_${name.toUpperCase()}`,
          reducer: ActionPatterns.item.clear<T> as (
            state: unknown,
            payload: unknown
          ) => unknown,
        },
        {
          name: `update${name.charAt(0).toUpperCase() + name.slice(1)}`,
          type: `UPDATE_${name.toUpperCase()}`,
          reducer: ActionPatterns.item.update<T> as (
            state: unknown,
            payload: unknown
          ) => unknown,
        },
      ],
    },
  };
}

/**
 * Creates a simple boolean state configuration
 */
export function createBooleanState(
  name: string,
  defaultValue: boolean = false
): Record<string, { default: boolean; actions: ActionConfig[] }> {
  return {
    [name]: {
      default: defaultValue,
      actions: [
        {
          name: `set${name.charAt(0).toUpperCase() + name.slice(1)}`,
          type: `SET_${name.toUpperCase()}`,
          reducer: ActionPatterns.boolean.set as (
            state: unknown,
            payload: unknown
          ) => unknown,
        },
        {
          name: `toggle${name.charAt(0).toUpperCase() + name.slice(1)}`,
          type: `TOGGLE_${name.toUpperCase()}`,
          reducer: ActionPatterns.boolean.toggle as (
            state: unknown,
            payload: unknown
          ) => unknown,
        },
        {
          name: `enable${name.charAt(0).toUpperCase() + name.slice(1)}`,
          type: `ENABLE_${name.toUpperCase()}`,
          reducer: ActionPatterns.boolean.enable as (
            state: unknown,
            payload: unknown
          ) => unknown,
        },
        {
          name: `disable${name.charAt(0).toUpperCase() + name.slice(1)}`,
          type: `DISABLE_${name.toUpperCase()}`,
          reducer: ActionPatterns.boolean.disable as (
            state: unknown,
            payload: unknown
          ) => unknown,
        },
      ],
    },
  };
}

/**
 * Creates a simple string state configuration
 */
export function createStringState(
  name: string,
  defaultValue: string = ""
): Record<string, { default: string; actions: ActionConfig[] }> {
  return {
    [name]: {
      default: defaultValue,
      actions: [
        {
          name: `set${name.charAt(0).toUpperCase() + name.slice(1)}`,
          type: `SET_${name.toUpperCase()}`,
          reducer: ActionPatterns.string.set as (
            state: unknown,
            payload: unknown
          ) => unknown,
        },
        {
          name: `clear${name.charAt(0).toUpperCase() + name.slice(1)}`,
          type: `CLEAR_${name.toUpperCase()}`,
          reducer: ActionPatterns.string.clear as (
            state: unknown,
            payload: unknown
          ) => unknown,
        },
      ],
    },
  };
}

/**
 * Creates a simple number state configuration
 */
export function createNumberState(
  name: string,
  defaultValue: number = 0
): Record<string, { default: number; actions: ActionConfig[] }> {
  return {
    [name]: {
      default: defaultValue,
      actions: [
        {
          name: `set${name.charAt(0).toUpperCase() + name.slice(1)}`,
          type: `SET_${name.toUpperCase()}`,
          reducer: ActionPatterns.number.set as (
            state: unknown,
            payload: unknown
          ) => unknown,
        },
        {
          name: `increment${name.charAt(0).toUpperCase() + name.slice(1)}`,
          type: `INCREMENT_${name.toUpperCase()}`,
          reducer: ActionPatterns.number.increment as (
            state: unknown,
            payload: unknown
          ) => unknown,
        },
        {
          name: `decrement${name.charAt(0).toUpperCase() + name.slice(1)}`,
          type: `DECREMENT_${name.toUpperCase()}`,
          reducer: ActionPatterns.number.decrement as (
            state: unknown,
            payload: unknown
          ) => unknown,
        },
        {
          name: `reset${name.charAt(0).toUpperCase() + name.slice(1)}`,
          type: `RESET_${name.toUpperCase()}`,
          reducer: ActionPatterns.number.reset as (
            state: unknown,
            payload: unknown
          ) => unknown,
        },
      ],
    },
  };
}
